import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";
import { Filter } from 'react-bootstrap-icons';
import { TokenManager } from "../utils/tokenManager";

const fetchChallans = async (pageNumber = 0, filters, setChallans, setTotalPages, setPage, size) => {
  try {
    const token = localStorage.getItem("token");
    
    if (!token) {
      console.error("No authentication token found");
      setChallans([]);
      return;
    }

    const params = new URLSearchParams();

    if (filters.name) params.append("name", filters.name);
    if (filters.challanType) params.append("challanType", filters.challanType);
    if (filters.fromDate) params.append("fromDate", filters.fromDate);
    if (filters.toDate) params.append("toDate", filters.toDate);

    params.append("page", pageNumber);
    params.append("size", size);

    const res = await fetch(
      `${BASE_URL}${API_ENDPOINTS.CHALLANS.SEARCH}?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }

    const data = await res.json();
    console.log("API Response:", data);

    setChallans(data.content || data || []);
    setTotalPages(data.totalPages || data.totalPages || 0);
    
    // Handle different response structures for page number
    const currentPage = data.number !== undefined ? data.number : (data.currentPage !== undefined ? data.currentPage : pageNumber);
    setPage(currentPage);

  } catch (error) {
    console.error("Fetch error:", error);
    toast.error("Failed to load challans");
    setChallans([]);
  }
};

function ViewChallan() {

  const navigate = useNavigate();
  const [challans, setChallans] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [size] = useState(10);
  const [filters, setFilters] = useState({
    name: "",
    challanType: "",
    fromDate: "",
    toDate: ""
  });

  useEffect(() => {
    fetchChallans(page, filters, setChallans, setTotalPages, setPage, size);
  }, [page]);

  /* ================= AUTO SEARCH ================= */

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(0);
      fetchChallans(0, filters, setChallans, setTotalPages, setPage, size);
    }, 500);

    return () => clearTimeout(delay);
  }, [filters]);

  /* ================= RESET ================= */

  const resetFilters = () => {
    const reset = {
      name: "",
      challanType: "",
      fromDate: "",
      toDate: ""
    };

    setFilters(reset);
    setPage(0);
    fetchChallans(0, reset, setChallans, setTotalPages, setPage, size);
  };

  /* ================= HANDLE INPUT ================= */

  const handleChange = (e) => {

    const { name, value } = e.target;

    setFilters(prev => ({
      ...prev,
      [name]: value
    }));

  };

  /* ================= PAGINATION ================= */

  const nextPage = () => {
    if (page < totalPages - 1) {
      setPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (page > 0) {
      setPage(prev => prev - 1);
    }
  };

  /* ================= ACTIONS ================= */

  const editChallan = (id) => {
    navigate(`/dashboard/challan/edit/${id}`);
  };

  const deleteChallan = async (id) => {

    if (!window.confirm("Delete this challan?")) return;

    try {

      const token = localStorage.getItem("token");

      await fetch(
        `${BASE_URL}${API_ENDPOINTS.CHALLANS.DELETE_BY_ID(id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Challan deleted successfully");

      fetchChallans(page, filters, setChallans, setTotalPages, setPage, size);

    } catch {

      toast.error("Delete failed");

    }

  };

  const viewPdf = async (id) => {
    try {
      const token = TokenManager.getToken();
      if (!token) {
        toast.error("Please login to download PDF");
        return;
      }

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.CHALLANS.DOWNLOAD(id)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Create download link
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `challan-${id}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);
      
      toast.success("PDF downloaded successfully");
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error("Failed to download PDF");
      
      // Fallback: Try opening in new window
      try {
        window.open(`${BASE_URL}${API_ENDPOINTS.CHALLANS.DOWNLOAD(id)}`, '_blank');
        toast.info("Opening PDF in new window");
      } catch (fallbackError) {
        toast.error("Unable to download or open PDF");
      }
    }
  };

  const sharePdf = async (id) => {
    try {
      const url = `${BASE_URL}${API_ENDPOINTS.CHALLANS.DOWNLOAD(id)}`;
      
      // Get auth token
      const token = TokenManager.getToken();
      if (!token) {
        toast.error("Session expired. Please login again.");
        return;
      }
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Download the PDF with authentication
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Check if blob is actually a PDF
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      // Create download link
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `challan-${id}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);
      
      toast.success("PDF downloaded successfully");
      
      // Optional: Try to share if Web Share API is available
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], `challan-${id}.pdf`, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Challan ${id}`,
              text: 'Challan PDF',
              files: [file]
            });
          }
        } catch (shareError) {
          console.log('Native sharing not available, download completed');
        }
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
      
      // Fallback: Open PDF in new window
      try {
        window.open(`${BASE_URL}${API_ENDPOINTS.CHALLANS.DOWNLOAD(id)}`, '_blank');
        toast.info("Opening PDF in new window");
      } catch (fallbackError) {
        toast.error("Unable to download or open PDF");
      }
    }
  };

  const navigateToPassbook = async (customerId, customerName) => {
  try {
    console.log(`Fetching passbook for customer: ${customerName} (ID: ${customerId})}`);
    
    // Call the passbook API
    const response = await fetch(`${BASE_URL}/challans/passbook?name=${encodeURIComponent(customerName)}&page=0&size=10`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch passbook data');
    }
    
    const passbookData = await response.json();

    // Fetch the Passbook Summary (Product Balances)
    const summaryResponse = await fetch(`${BASE_URL}/challans/getPassbookSummary?id=${customerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    const summaryData = summaryResponse.ok ? await summaryResponse.json() : [];

    console.log("Passbook data:", passbookData);
    console.log("Passbook summary:", summaryData);
    
    // Store passbook data
    localStorage.setItem('passbookData', JSON.stringify(passbookData));
    localStorage.setItem('passbookSummary', JSON.stringify(summaryData));
    
    // Show success message with data count
    toast.success(`Found ${passbookData.content?.length || 0} challans in passbook`);
    
    // Navigate to passbook page with data
    console.log("Navigating to passbook page with data:", {
      customerId,
      customerName,
      passbookData: passbookData
    });
    
    // Check if we have data before navigating
    if (passbookData && passbookData.content) {
      console.log("Passbook data summary:", {
        totalChallans: passbookData.content.length,
        totalPages: passbookData.totalPages,
        currentPage: passbookData.pageable?.pageNumber
      });
    }
    
    const navigationState = {
      customerId: customerId,
      customerName: customerName,
      fromViewChallan: true,
      passbookData: passbookData,
      summaryData: summaryData
    };
    
    console.log("Navigation state being passed:", navigationState);
    
    navigate(`/dashboard/customers/passbook/${customerId}`, {
      state: navigationState
    });
    
    console.log("Navigation completed to passbook page");
    
  } catch (error) {
    console.error('Error fetching passbook:', error);
    toast.error("Failed to fetch passbook data");
    
    console.log("Attempting fallback navigation without data");
    
    // Fallback: Navigate without data
    try {
      navigate(`/dashboard/customers/passbook/${customerId}`, {
        state: {
          customerId: customerId,
          customerName: customerName,
          fromViewChallan: true
        }
      });
      console.log("Fallback navigation completed");
    } catch (navError) {
      console.error("Navigation error:", navError);
      toast.error("Unable to navigate to passbook page");
      
      // Last resort: Try a simple navigation
      navigate(`/dashboard/customers/passbook/${customerId}`);
    }
  }
};

const viewGoods = async (challan) => {
  try {
    console.log("Goods button clicked for challan:", challan);
    
    // Step 1: Get challan details by ID
    const challanResponse = await fetch(`${BASE_URL}/api/v1/challans/getByChallanId/${challan.id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!challanResponse.ok) {
      throw new Error('Failed to fetch challan details');
    }
    
    const challanDetails = await challanResponse.json();
    console.log("Challan details:", challanDetails);
    
    // Step 2: Get cylinder serial numbers for each item
    const cylinderData = [];
    
    if (challanDetails.items && Array.isArray(challanDetails.items)) {
      for (const item of challanDetails.items) {
        if (item.name) {
          try {
            const cylinderResponse = await fetch(`${BASE_URL}/api/v1/cylinderMaster/getSrno?cylinderName=${encodeURIComponent(item.name)}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (cylinderResponse.ok) {
              const cylinderSrNos = await cylinderResponse.json();
              console.log(`Cylinder SR numbers for ${item.name}:`, cylinderSrNos);
              
              cylinderData.push({
                itemName: item.name,
                srNumbers: cylinderSrNos,
                deliveredQty: item.deliveredQty || 0,
                receivedQty: item.receivedQty || 0,
                batchNo: item.batchNo || []
              });
            }
          } catch (error) {
            console.error(`Error fetching cylinder data for ${item.name}:`, error);
          }
        }
      }
    }
    
    // Step 3: Show data directly (you can customize this part)
    console.log("Complete cylinder data:", cylinderData);
    
    // Store data for display
    localStorage.setItem('challanGoodsData', JSON.stringify({
      challan: challanDetails,
      cylinders: cylinderData
    }));
    
    // Show success message with data count
    toast.success(`Found ${cylinderData.length} items with serial numbers`);
    
    // Display data in console for now (you can modify to show in UI)
    cylinderData.forEach((item, index) => {
      console.log(`Item ${index + 1}:`);
      console.log(`  Name: ${item.itemName}`);
      console.log(`  SR Numbers: ${item.srNumbers.join(', ')}`);
      console.log(`  Delivered: ${item.deliveredQty}`);
      console.log(`  Received: ${item.receivedQty}`);
      console.log(`  Batch: ${item.batchNo.join(', ')}`);
    });
    
    // Optional: Navigate to goods page with data
    navigate(`/dashboard/customers/goods/${challan.customerId}`, { 
      state: { 
        challan: challanDetails, 
        cylinders: cylinderData,
        originalChallan: challan 
      } 
    });
    
  } catch (error) {
    console.error('Error fetching goods data:', error);
    toast.error("Failed to fetch goods data");
    
    // Fallback: Navigate without data
    navigate(`/dashboard/customers/goods/${challan.customerId}`);
  }
};

  /* ================= QTY CALC ================= */

  const getTotalQty = (items) => {
  if (!Array.isArray(items)) return 0;

  return items.reduce((sum, item) => {
    const deliveredQty = item.deliveredQty || 0;
    const receivedQty = item.receivedQty || 0;
    return sum + deliveredQty + receivedQty;
  }, 0);
};

const openPassport = (customerName) => {
  // Function to open customer passport/passbook
  console.log(`Opening passport/passbook for: ${customerName}`);
  // You can add navigation logic here
  // window.open(`/passport/${customerName}`);
};

  /* ================= UI ================= */

  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '100vw',
      overflowX: 'hidden',
      minHeight: '100vh',
      boxSizing: 'border-box'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          flex: '1', 
          minWidth: '200px',
          overflow: 'hidden'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>View Challan</h2>
        </div>
      <div>
        <button
            onClick={() => setShowFilters(!showFilters)}
            style={{ 
              padding: '10px 20px',
              border: `2px solid ${showFilters ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: '8px',
              backgroundColor: showFilters ? '#3b82f6' : '#ffffff',
              color: showFilters ? '#ffffff' : '#475569',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: showFilters 
                ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transform: showFilters ? 'translateY(-1px)' : 'translateY(0)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!showFilters) {
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.borderColor = '#3b82f6';
                e.target.style.color = '#3b82f6';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showFilters) {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#475569';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseDown={(e) => {
              e.target.style.transform = showFilters ? 'translateY(0)' : 'translateY(-2px)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = showFilters ? 'translateY(-1px)' : 'translateY(-1px)';
            }}
        >
          <Filter style={{ 
            fontSize: '16px',
            transition: 'transform 0.2s ease'
          }} />
          <span style={{ 
            display: 'none'
          }}>
            {showFilters ? 'Hide Filter' : 'Show Filter'}
          </span>
          {showFilters && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '8px',
              height: '8px',
              backgroundColor: '#ef4444',
              borderRadius: '50%',
              border: '2px solid #ffffff'
            }} />
          )}
        </button>
      </div>
      </div>

      {/* SPACER */}
      <div style={{ marginBottom: '24px' }}></div>

      {/* TABLE HEADER - Separate Component */}
      {showFilters && (
        <div style={{ 
          padding: '20px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          marginBottom: '20px',
          overflowX: 'auto'
        }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: window.innerWidth > 768 ? 'repeat(auto-fit, minmax(180px, 1fr))' : '1fr',
            gap: '16px',
            alignItems: 'end',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                color: '#374151',
                letterSpacing: '0.025em'
              }}>Customer Name</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleChange}
                  onClick={() => openPassport(filters.name)}
                  placeholder="Search customer..."
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px 16px 12px 44px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: '500',
                    color: '#1f2937',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '16px',
                  pointerEvents: 'none'
                }}>
                  👤
                </div>
              </div>
            </div>
            <div>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                color: '#374151',
                letterSpacing: '0.025em'
              }}>Type</label>
              <div style={{ position: 'relative' }}>
                <select
                  name="challanType"
                  value={filters.challanType}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px 40px 12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: '500',
                    color: '#1f2937',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">📋 All</option>
                  <option value="DELIVERED">📤 Delivered</option>
                  <option value="RECEIVED">📥 Received</option>
                </select>
                <div style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '12px',
                  pointerEvents: 'none'
                }}>
                  ▼
                </div>
              </div>
            </div>
            <div>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                color: '#374151',
                letterSpacing: '0.025em'
              }}>From Date</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px 16px 12px 44px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: '500',
                    color: '#1f2937',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '16px',
                  pointerEvents: 'none'
                }}>
                  📅
                </div>
              </div>
            </div>
            <div>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                color: '#374151',
                letterSpacing: '0.025em'
              }}>To Date</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px 16px 12px 44px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                    fontWeight: '500',
                    color: '#1f2937',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '16px',
                  pointerEvents: 'none'
                }}>
                  📅
                </div>
              </div>
            </div>
            <div style={{ 
              display: 'flex',
              gap: '12px',
              gridColumn: window.innerWidth > 768 ? 'auto' : '1 / -1',
              justifyContent: window.innerWidth > 768 ? 'flex-start' : 'center',
              alignItems: 'flex-end'
            }}>
              <button 
                onClick={() => fetchChallans(0, filters, setChallans, setTotalPages, setPage, size)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                  fontWeight: '500',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.borderColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.borderColor = '#3b82f6';
                }}
              >
                Apply
              </button>
              <button 
                onClick={resetFilters}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                  fontWeight: '500',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.color = '#6b7280';
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
      {/* TABLE HEADER - Separate Component */}
      <div style={{ 
        padding: '6px 12px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '6px',
        marginBottom: '16px',
        fontSize: 'clamp(0.875rem, 2vw, 1rem)',
        fontWeight: '600',
        color: '#1e293b',
        border: '1px solid #3b82f6',
        boxShadow: '0 1px 4px rgba(59, 130, 246, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        width: 'fit-content'
      }}>
        <div style={{
          width: '4px',
          height: '4px',
          backgroundColor: challans.length > 0 ? '#22c55e' : '#64748b',
          borderRadius: '50%',
          animation: challans.length > 0 ? 'pulse 2s infinite' : 'none'
        }} />
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          alignItems: 'baseline'
        }}>
          <span style={{
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            fontWeight: '700',
            color: '#1e293b',
            lineHeight: '1'
          }}>
            {challans.length.toLocaleString()}
          </span>
          <span style={{
            fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
            fontWeight: '500',
            color: '#64748b',
            lineHeight: '1',
            textTransform: 'lowercase',
            letterSpacing: '0.025em'
          }}>
            {challans.length === 1 ? 'Record' : 'Records'}
          </span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
          // Removed marginLeft: 'auto' to make it more compact
        }}>
          <div style={{
            padding: '1px 4px',
            backgroundColor: challans.length > 0 ? '#dcfce7' : '#f1f5f9',
            color: challans.length > 0 ? '#166534' : '#64748b',
            borderRadius: '3px',
            fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)',
            fontWeight: '500',
            border: `1px solid ${challans.length > 0 ? '#bbf7d0' : '#e2e8f0'}`
          }}>
            {/* {challans.length > 0 ? 'Data Available' : 'No Data'} */}
          </div>
        </div>
        <style jsx>{`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
            }
          }
        `}</style>
      </div>
      
      {/* TABLE */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          maxWidth: '100%'
        }}>
          <table style={{ 
            width: '100%',
            minWidth: '600px',
            maxWidth: '100%',
            borderCollapse: 'collapse',
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            tableLayout: 'auto'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '140px'
                }}>Customer Name</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '100px'
                }}>Challan No</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '60px'
                }}>Type</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '80px'
                }}>Location</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '60px'
                }}>Qty</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '80px'
                }}>Date</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '80px'
                }}>Created By</th>
                <th style={{ 
                  padding: '8px 6px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: 'bold',
                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                  whiteSpace: 'nowrap',
                  minWidth: '200px'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {challans.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ 
                    padding: '40px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    color: '#6c757d',
                    fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                  }}>
                    No challans found
                  </td>
                </tr>
              ) : (
                challans.map(c => (
                  <tr key={c.id}>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '140px',
                      textAlign: 'center'
                    }}>
                      <button 
                        onClick={() => navigateToPassbook(c.customerId, c.customerName)}
                        title={`View passbook for ${c.customerName}`}
                        aria-label={`View passbook for ${c.customerName}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#000000',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          outline: 'none',
                          textDecoration: 'underline',
                          textDecorationColor: 'transparent',
                          transition: 'color 0.2s ease, textDecorationColor 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#007bff';
                          e.target.style.textDecorationColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#000000';
                          e.target.style.textDecorationColor = 'transparent';
                        }}
                        onFocus={(e) => {
                          e.target.style.color = '#007bff';
                          e.target.style.textDecorationColor = '#007bff';
                        }}
                        onBlur={(e) => {
                          e.target.style.color = '#000000';
                          e.target.style.textDecorationColor = 'transparent';
                        }}
                      >
                        {c.customerName}
                      </button>
                    </td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '100px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'center'
                    }}>{c.challanNumber || 'N/A'}</td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '60px',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '12px',
                        fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                        backgroundColor: c.challanType?.toUpperCase() === "DELIVERED" ? '#d4edda' : '#cce7ff',
                        color: c.challanType?.toUpperCase() === "DELIVERED" ? '#155724' : '#004085',
                        whiteSpace: 'nowrap'
                      }}>
                        {c.challanType}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'center'
                    }}>{c.siteLocation || '—'}</td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '60px',
                      textAlign: 'center'
                    }}>{getTotalQty(c.items)}</td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '80px',
                      textAlign: 'center'
                    }}>{new Date(c.date).toLocaleDateString('en-IN')}</td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'center'
                    }}>{c.createdBy || '—'}</td>
                    <td style={{ 
                      padding: '8px 6px',
                      borderBottom: '1px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      minWidth: '200px',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        gap: '4px',
                        flexWrap: window.innerWidth > 768 ? 'nowrap' : 'wrap',
                        justifyContent: 'center'
                      }}>
                      <button 
                        onClick={() => viewGoods(c)} 
                        title="Goods" 
                        aria-label="View goods"
                        style={{
                          padding: '6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#6366f1',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          minWidth: window.innerWidth > 768 ? '32px' : '28px',
                          height: window.innerWidth > 768 ? '32px' : '28px'
                        }}
                      >
                        <i className="bi bi-box-seam"></i>
                      </button>
                      <button 
                        onClick={() => editChallan(c.id)} 
                        title="Edit" 
                        aria-label="Edit challan"
                        style={{
                          padding: '6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          minWidth: window.innerWidth > 768 ? '32px' : '28px',
                          height: window.innerWidth > 768 ? '32px' : '28px'
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button 
                        onClick={() => deleteChallan(c.id)} 
                        title="Delete" 
                        aria-label="Delete challan"
                        style={{
                          padding: '6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          minWidth: window.innerWidth > 768 ? '32px' : '28px',
                          height: window.innerWidth > 768 ? '32px' : '28px'
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                      <button 
                        onClick={() => sharePdf(c.id)} 
                        title="Share" 
                        aria-label="Share PDF"
                        style={{
                          padding: '6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          minWidth: window.innerWidth > 768 ? '32px' : '28px',
                          height: window.innerWidth > 768 ? '32px' : '28px'
                        }}
                      >
                        <i className="bi bi-share"></i>
                      </button>
                      <button 
                        onClick={() => viewPdf(c.id)} 
                        title="View" 
                        aria-label="View PDF"
                        style={{
                          padding: '6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          minWidth: window.innerWidth > 768 ? '32px' : '28px',
                          height: window.innerWidth > 768 ? '32px' : '28px'
                        }}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          padding: '20px',
          flexWrap: window.innerWidth > 768 ? 'nowrap' : 'wrap',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <button
            onClick={prevPage}
            disabled={page === 0 || totalPages === 0 || challans.length === 0}
            style={{
              padding: '10px 20px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: page === 0 || totalPages === 0 || challans.length === 0 ? '#f8f9fa' : '#007bff',
              color: page === 0 || totalPages === 0 || challans.length === 0 ? '#6c757d' : 'white',
              cursor: page === 0 || totalPages === 0 || challans.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              minWidth: '80px',
              maxWidth: '120px',
              boxSizing: 'border-box'
            }}
          >
            Previous
          </button>

          <span style={{ 
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            color: '#6c757d',
            fontWeight: '500'
          }}>
            Page {totalPages > 0 ? page + 1 : 0} of {totalPages > 0 ? totalPages : 0}
          </span>

          <button
            onClick={nextPage}
            disabled={page === totalPages - 1 || totalPages === 0 || totalPages <= 1 || challans.length === 0}
            style={{
              padding: '10px 20px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: page === totalPages - 1 || totalPages === 0 || totalPages <= 1 || challans.length === 0 ? '#f8f9fa' : '#007bff',
              color: page === totalPages - 1 || totalPages === 0 || totalPages <= 1 || challans.length === 0 ? '#6c757d' : 'white',
              cursor: page === totalPages - 1 || totalPages === 0 || totalPages <= 1 || challans.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              minWidth: '80px',
              maxWidth: '120px',
              boxSizing: 'border-box'
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewChallan;